import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "placement", "onOpenChange", "isOpen", "renderExtraOptions"];
import { CardMenu, CardMenuOptions } from './CardMenu';
import { CardMenuChangePlaylistThumbnailOption } from '../../savedItems/components/CardMenuChangePlaylistThumbnailOption';
import CardMenuAddToPlaylistOption from '../../savedItems/components/CardMenuAddToPlaylistOption';
import CardMenuAddToNewPlaylistOption from '../../savedItems/components/CardMenuAddToNewPlaylistOption';
import CardMenuWatchLaterOption from '../../savedItems/components/CardMenuWatchLaterOption';
import { EditPlaylistButton } from '../../savedItems/components/EditPlaylistButton';
import { useUserOwnsPlaylist } from '../../savedItems/savedItemsQueries';
import FormattedMessage from 'I18n/components/FormattedMessage';
import StartPlaylistButton from '../../savedItems/components/StartPlaylistButton';
import UsageTracker from 'academy-common-lib/utils/UsageTracker';
import { SocialShareMenuOption } from '../../share/components/SocialShareMenuOption';
import { DuplicatePlaylistButton } from '../../savedItems/components/DuplicatePlaylistButton';
import { ArchivePlaylistButton, ArchivePlaylistsToggleIconMessage } from '../../myPlaylists/components/ArchivePlaylistButton';
import { SavePlaylistButton, SavePlaylistsToggleIconMessage } from '../../myPlaylists/components/SavePlaylistButton';
import { AssignPlaylistButton } from '../../playlistAssignments/components/AssignPlaylistButton';
import { CardMenuUnassignPlaylistOption } from '../../playlistAssignments/components/CardMenuUnassignPlaylistOption';
import { CopyEmbedCodeMenuOption } from '../../share/components/CopyEmbedCodeMenuOption';
import { CreateHubspotPlaylistButton } from '../../hubspotPlaylists/components/createOrEditHubspotPlaylist/CreateHubspotPlaylistButton';
import { useHasScope } from '../../accounts/accountsQueries';
import EditHubspotPlaylistButton from '../../hubspotPlaylists/components/createOrEditHubspotPlaylist/EditHubspotPlaylistButton';
import ViewAuditLogButton from '../../hubspotPlaylists/components/auditLog/ViewAuditLogButton';
import { useCanUserEditPlaylist, useIsHubspotPlaylist } from '../../savedItems/savedItemsHooks';
import { SideNavItem } from 'foundations-components/transitional/components/SideNav';
import { HR } from 'foundations-components/transitional/components/HR';
import { Flex } from 'foundations-components/components/Flex';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import CardMenuMyLearningOption from './CardMenuMyLearningOption';
import useNavigate from 'academy-common-lib/hooks/useNavigate';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import { isShortCardModel, isTrackOrLessonCardModel } from 'academy-common-lib/features/cards/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const trackCreateNewPlaylist = (itemId, type, visibility) => {
  const itemVisibility = visibility ? readUnhandledEnumCase(visibility) : undefined;
  UsageTracker.trackStandalone('Playlist Interaction', {
    action: 'add-to-new-playlist',
    location: 'card-menu',
    itemId,
    itemType: type,
    visibility: itemVisibility
  });
};
const trackAddToPlaylist = (itemId, type, visibility) => {
  const itemVisibility = visibility ? readUnhandledEnumCase(visibility) : undefined;
  UsageTracker.trackStandalone('Playlist Interaction', {
    action: 'add-to-playlist',
    location: 'card-menu',
    itemId,
    itemType: type,
    visibility: itemVisibility
  });
};
const trackUnassignedPlaylist = () => {
  UsageTracker.trackStandalone('Playlist Interaction', {
    action: 'playlist-unassigned',
    location: 'card-menu'
  });
};
const TrackLessonMenuOptions = ({
  item,
  parentPlaylist,
  parentTrack,
  closeModal,
  overviewType
}) => {
  const showIfMyLearning = overviewType === 'my-learning' || overviewType === 'in-progress-section';
  const visibility = item.visibility;
  const onCreateNewClick = () => {
    trackCreateNewPlaylist(item.id, item.type, visibility);
  };
  const onAddToPlaylistClick = () => {
    trackAddToPlaylist(item.id, item.type, visibility);
    closeModal();
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(CardMenuWatchLaterOption, {
      item: item,
      onClick: closeModal
    }), /*#__PURE__*/_jsx(CardMenuAddToPlaylistOption, {
      item: {
        id: item.id,
        type: item.type,
        language: item.language
      },
      onCreatePlaylistClick: onCreateNewClick,
      onPlaylistClick: onAddToPlaylistClick
    }), /*#__PURE__*/_jsx(CardMenuAddToNewPlaylistOption, {
      item: item
    }), /*#__PURE__*/_jsx(HR, {
      distance: "extra-small"
    }), /*#__PURE__*/_jsx(SocialShareMenuOption, {
      item: item,
      onShare: closeModal,
      parentPlaylist: parentPlaylist,
      parentTrack: parentTrack,
      shareButtonLabel: parentPlaylist || parentTrack ? undefined : item.type === 'TRACK' ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "academyCommon.shareThis.shareTrack"
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "academyCommon.shareThis.shareLesson"
      })
    }), /*#__PURE__*/_jsx(CopyEmbedCodeMenuOption, {
      language: item.language
    }), showIfMyLearning && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(HR, {
        distance: "extra-small"
      }), /*#__PURE__*/_jsx(CardMenuMyLearningOption, {
        item: item,
        onClick: closeModal
      })]
    })]
  });
};
const PlaylistMenuOptions = ({
  item,
  closeModal,
  overviewType
}) => {
  const {
    id: playlistId
  } = item;
  const navigate = useNavigate();
  const userIsOwner = useUserOwnsPlaylist(playlistId);
  const userCanEditPlaylist = useCanUserEditPlaylist(item.id);
  const isHubspotPlaylist = useIsHubspotPlaylist(item.id);
  const {
    hasScope: hasPublicPlaylistsAdminScope
  } = useHasScope('academy-public-playlists-admin');
  const showIfMyLearning = overviewType === 'my-learning' || overviewType === 'in-progress-section';
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(StartPlaylistButton, {
      appearsIn: "playlist",
      playlistId: playlistId,
      render: ({
        url,
        onClick,
        buttonText,
        isLoading,
        isDisabled
      }) => /*#__PURE__*/_jsx(SideNavItem, {
        disabled: isDisabled,
        onClick: () => {
          if (isDisabled) return;
          onClick();
          navigate(url);
        },
        role: "navigation-item",
        title: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          justifyContent: "space-between",
          children: [/*#__PURE__*/_jsx("span", {
            style: {
              opacity: isLoading || isDisabled ? 0.3 : 1
            },
            children: buttonText
          }), isLoading && /*#__PURE__*/_jsx(LoadingSpinner, {
            margin: "0",
            size: "extra-small"
          })]
        })
      }),
      showLoader: true
    }), userCanEditPlaylist && /*#__PURE__*/_jsxs(_Fragment, {
      children: [isHubspotPlaylist && hasPublicPlaylistsAdminScope ? /*#__PURE__*/_jsx(EditHubspotPlaylistButton, {
        playlistId: playlistId,
        render: onClick => /*#__PURE__*/_jsx(SideNavItem, {
          onClick: onClick,
          role: "navigation-item",
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "academyCommon.playlists.hubspotPlaylists.createEditModal.editPlaylist"
          })
        })
      }) : /*#__PURE__*/_jsx(EditPlaylistButton, {
        playlistId: playlistId,
        render: onClick => /*#__PURE__*/_jsx(SideNavItem, {
          onClick: onClick,
          role: "navigation-item",
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "academyCommon.playlists.editDetails"
          })
        })
      }), /*#__PURE__*/_jsx(CardMenuChangePlaylistThumbnailOption, {
        onItemClick: closeModal,
        playlistId: playlistId
      }), /*#__PURE__*/_jsx(HR, {
        distance: "extra-small"
      })]
    }), isHubspotPlaylist && hasPublicPlaylistsAdminScope && /*#__PURE__*/_jsx(ViewAuditLogButton, {
      playlistId: playlistId,
      render: onClick => /*#__PURE__*/_jsx(SideNavItem, {
        onClick: onClick,
        role: "navigation-item",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyCommon.playlists.hubspotPlaylists.auditLog.viewAuditLog"
        })
      })
    }), /*#__PURE__*/_jsx(SocialShareMenuOption, {
      item: item,
      onShare: closeModal,
      shareButtonLabel: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "academyCommon.shareThis.sharePlaylist"
      })
    }), /*#__PURE__*/_jsx(AssignPlaylistButton, {
      playlistId: playlistId,
      render: onClick => /*#__PURE__*/_jsx(SideNavItem, {
        onClick: onClick,
        role: "navigation-item",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyCommon.playlistAssignments.assignPlaylist"
        })
      })
    }), /*#__PURE__*/_jsx(CardMenuUnassignPlaylistOption, {
      onUnassigned: () => {
        trackUnassignedPlaylist();
        closeModal();
      },
      playlistId: playlistId
    }), /*#__PURE__*/_jsx(HR, {
      distance: "extra-small"
    }), /*#__PURE__*/_jsx(DuplicatePlaylistButton, {
      playlistId: playlistId,
      render: onClick => /*#__PURE__*/_jsx(SideNavItem, {
        onClick: onClick,
        role: "navigation-item",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyCommon.playlists.duplicatePlaylist"
        })
      })
    }), !isHubspotPlaylist && hasPublicPlaylistsAdminScope && /*#__PURE__*/_jsx(CreateHubspotPlaylistButton, {
      playlistId: playlistId,
      render: onClick => /*#__PURE__*/_jsx(SideNavItem, {
        onClick: onClick,
        role: "navigation-item",
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyCommon.playlists.hubspotPlaylists.createEditModal.createPlaylist"
        })
      })
    }), /*#__PURE__*/_jsx(HR, {
      distance: "extra-small"
    }), /*#__PURE__*/_jsx(SavePlaylistButton, {
      onSuccess: closeModal,
      playlistId: playlistId,
      render: ({
        onClick,
        isSaved
      }) => /*#__PURE__*/_jsx(SideNavItem, {
        onClick: onClick,
        role: "navigation-item",
        title: /*#__PURE__*/_jsx(SavePlaylistsToggleIconMessage, {
          isSaved: isSaved
        })
      })
    }), userIsOwner && /*#__PURE__*/_jsx(ArchivePlaylistButton, {
      onSuccess: closeModal,
      playlistId: playlistId,
      render: ({
        onClick,
        isArchived
      }) => /*#__PURE__*/_jsx(SideNavItem, {
        onClick: onClick,
        role: "navigation-item",
        title: /*#__PURE__*/_jsx(ArchivePlaylistsToggleIconMessage, {
          isArchived: isArchived
        })
      })
    }), showIfMyLearning && /*#__PURE__*/_jsx(CardMenuMyLearningOption, {
      item: item,
      onClick: closeModal
    })]
  });
};
const ShortMenuOptions = ({
  item,
  closeModal,
  overviewType
}) => {
  const {
    id: shortId
  } = item;
  const showIfMyLearning = overviewType === 'my-learning' || overviewType === 'in-progress-section';
  const visibility = item.visibility;
  const onCreateNewClick = () => {
    trackCreateNewPlaylist(shortId, 'SHORT_FORM_CONTENT', visibility);
  };
  const onAddToPlaylistClick = () => {
    trackAddToPlaylist(shortId, 'SHORT_FORM_CONTENT', visibility);
    closeModal();
  };
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(CardMenuWatchLaterOption, {
      item: item,
      onClick: closeModal
    }), /*#__PURE__*/_jsx(CardMenuAddToPlaylistOption, {
      item: {
        id: item.id,
        type: item.type,
        language: item.language
      },
      onCreatePlaylistClick: onCreateNewClick,
      onPlaylistClick: onAddToPlaylistClick
    }), /*#__PURE__*/_jsx(CardMenuAddToNewPlaylistOption, {
      item: item
    }), /*#__PURE__*/_jsx(HR, {
      distance: "extra-small"
    }), /*#__PURE__*/_jsx(SocialShareMenuOption, {
      item: item,
      onShare: closeModal
      // TODO: Re-enable parentPlaylist after migration - see https://github.com/HubSpotEngineering/LearningCenter/issues/1194
      // parentPlaylist={parentPlaylist}
      ,
      shareButtonLabel:
      /*#__PURE__*/
      // TODO: Re-enable conditional label after migration - see https://github.com/HubSpotEngineering/LearningCenter/issues/1194
      // parentPlaylist ? undefined : (
      _jsx(FormattedMessage, {
        message: "academyCommon.shareThis.shareShort"
      })
      // )
    }), /*#__PURE__*/_jsx(CopyEmbedCodeMenuOption, {
      language: item.language
    }), showIfMyLearning && /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(HR, {
        distance: "extra-small"
      }), /*#__PURE__*/_jsx(CardMenuMyLearningOption, {
        item: item,
        onClick: closeModal
      })]
    })]
  });
};
const DesktopCardMenu = _ref => {
  let {
      children,
      placement = 'bottom right',
      onOpenChange,
      isOpen,
      renderExtraOptions
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    item
  } = props;
  const trackLessonItem = isTrackOrLessonCardModel(item) ? item : null;
  const shortItem = isShortCardModel(item) ? item : null;
  const handleOnOpenChange = e => onOpenChange(e.target.value);
  const closeModal = () => onOpenChange(false);
  return /*#__PURE__*/_jsx(CardMenu, {
    content: {
      body: /*#__PURE__*/_jsxs(CardMenuOptions, {
        children: [(item.type === 'PLAYLIST' || item.type === 'LEARNING_PATH') && /*#__PURE__*/_jsx(PlaylistMenuOptions, Object.assign({
          closeModal: closeModal
        }, props)), trackLessonItem && /*#__PURE__*/_jsx(TrackLessonMenuOptions, Object.assign({
          closeModal: closeModal
        }, props, {
          item: trackLessonItem
        })), shortItem && /*#__PURE__*/_jsx(ShortMenuOptions, Object.assign({
          closeModal: closeModal
        }, props, {
          item: shortItem
        })), renderExtraOptions && renderExtraOptions(onOpenChange)]
      })
    },
    onOpenChange: handleOnOpenChange,
    open: isOpen,
    placement: placement,
    children: children
  });
};
export default DesktopCardMenu;